import { useState, ChangeEvent, FocusEvent, MouseEvent } from 'react'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { useQuantityEntry } from '../useQuantityEntry'
import QuantityKeypad from './QuantityKeypad'

export type QuantityStepperProps = {
	value: number,
	/** Called with the clamped next value. */
	onChange: ( next: number ) => void,
	min?: number,
	max?: number,
	/** Accessible name for the control, e.g. the product being picked. */
	label: string,
	/**
	 * How many were called for. Shown beside the count so "how many do I need"
	 * and "how many have I got" are one glance rather than two.
	 */
	target?: number,
	/**
	 * Fixed width for the value itself, rather than letting it size to its
	 * digits. A single stepper is better sized to its content; a column of them
	 * has to line up, and `0/1` and `0/10` are not the same width.
	 */
	valueWidth?: number,
	disabled?: boolean,
}

function clamp( value: number, min: number, max: number ): number {
	return Math.min( Math.max( value, min ), max )
}

/**
 * Quantity entry: a minus/plus pair either side of the current value, and
 * behind the value itself whichever way of jumping straight to a number suits
 * the person's pointer — see `useQuantityEntry`.
 *
 * On a tablet that is an on-screen pad. A picker holding the tablet in one hand
 * cannot hit a small text input, and the operating system's own numeric
 * keyboard covers half the screen when they try.
 *
 * On a desktop it is the value itself, typed into directly. There is no
 * on-screen keyboard to get in the way, so a pad would be a popover in the way
 * of a keyboard the person already has their hands on.
 *
 * The minus/plus pair is the same either way, and is all a person needs for the
 * common case of being one or two out.
 */
export default function QuantityStepper( {
	value,
	onChange,
	min = 0,
	max = Number.MAX_SAFE_INTEGER,
	label,
	target,
	valueWidth,
	disabled = false,
}: QuantityStepperProps ) {
	const theme = useTheme()
	const entry = useQuantityEntry()
	const [ keypadAnchor, setKeypadAnchor ] = useState<HTMLElement | null>( null )
	// Digits typed since the keypad opened. Empty means "nothing typed yet", which
	// is what makes the first digit replace the current value instead of appending.
	const [ typed, setTyped ] = useState( '' )

	const openKeypad = ( event: MouseEvent<HTMLElement> ) => {
		setKeypadAnchor( event.currentTarget )
		setTyped( '' )
	}

	const closeKeypad = () => {
		setKeypadAnchor( null )
		setTyped( '' )
	}

	const commit = ( next: string ) => {
		setTyped( next )
		onChange( clamp( next === '' ? min : Number( next ), min, max ) )
	}

	// What the field shows while it has focus. Null means "show the value" — the
	// field only holds its own text mid-edit, so clearing it to type a fresh
	// number does not immediately snap back to the clamped value.
	const [ draft, setDraft ] = useState<string | null>( null )

	const handleFieldChange = ( event: ChangeEvent<HTMLInputElement> ) => {
		const digits = event.target.value.replace( /[^0-9]/g, '' )
		setDraft( digits )
		onChange( clamp( digits === '' ? min : Number( digits ), min, max ) )
	}

	// Whatever they left behind, the field goes back to showing the real value —
	// which is the clamped one, so typing 99 against a target of 4 settles to 4.
	const handleFieldBlur = () => setDraft( null )

	const selectAll = ( event: FocusEvent<HTMLInputElement> ) => event.target.select()

	return (
		<Stack direction="row" alignItems="center" spacing={`${theme.touch.gap}px`}>
			<IconButton
				aria-label={`One fewer ${label}`}
				disabled={disabled || value <= min}
				onClick={() => onChange( clamp( value - 1, min, max ) )}
				sx={{ border: 1, borderColor: 'pickFlow.cardBorder' }}
			>
				<RemoveIcon />
			</IconButton>

			{entry === 'keypad' ? (
				<Button
					variant="outlined"
					color="inherit"
					aria-label={`Quantity picked for ${label}`}
					disabled={disabled}
					onClick={openKeypad}
					sx={{
						minWidth: theme.touch.keypadKey,
						width: valueWidth,
						minHeight: theme.touch.control,
						borderColor: 'pickFlow.cardBorder',
						bgcolor: 'pickFlow.surface',
					}}
				>
					<Typography variant="h5" component="span">{value}</Typography>
					{target !== undefined && (
						<Typography
							variant="h6"
							component="span"
							color="text.secondary"
							sx={{ ml: 0.5, fontWeight: 400 }}
						>
							/{target}
						</Typography>
					)}
				</Button>
			) : (
				<TextField
					value={draft ?? String( value )}
					disabled={disabled}
					onChange={handleFieldChange}
					onBlur={handleFieldBlur}
					// Focusing selects, so the first keystroke replaces the count rather
					// than appending to it — the same thing the pad's first digit does.
					onFocus={selectAll}
					slotProps={{
						htmlInput: {
							'aria-label': `Quantity picked for ${label}`,
							'inputMode': 'numeric',
							'sx': { typography: 'h5', textAlign: 'right', px: 1 },
						},
						input: {
							endAdornment: target !== undefined
								? <InputAdornment position="end">/{target}</InputAdornment>
								: undefined,
							sx: { bgcolor: 'pickFlow.surface', minHeight: theme.touch.control },
						},
					}}
					sx={{ minWidth: theme.touch.keypadKey, width: valueWidth }}
				/>
			)}

			<IconButton
				aria-label={`One more ${label}`}
				disabled={disabled || value >= max}
				onClick={() => onChange( clamp( value + 1, min, max ) )}
				sx={{ border: 1, borderColor: 'pickFlow.cardBorder' }}
			>
				<AddIcon />
			</IconButton>

			<Popover
				open={entry === 'keypad' && !!keypadAnchor}
				anchorEl={keypadAnchor}
				onClose={closeKeypad}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				transformOrigin={{ vertical: 'top', horizontal: 'center' }}
			>
				<QuantityKeypad
					label={label}
					typed={typed}
					onTypedChange={commit}
					onDone={closeKeypad}
				/>
			</Popover>
		</Stack>
	)
}
